import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-semibold tracking-[-0.01em] whitespace-nowrap transition-all outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-btn hover:bg-primary/90 hover:shadow-btn-hover',
        primary: 'bg-ted-primary text-primary-foreground shadow-btn hover:bg-ted-primary/90 hover:shadow-btn-hover',
        primaryColor: 'bg-ted-primary text-primary-foreground shadow-btn hover:bg-ted-primary/90 hover:shadow-btn-hover',
        accent: 'bg-ted-sun text-ted-on-accent shadow-btn hover:bg-ted-sun/90 hover:shadow-btn-hover',
        destructive: 'bg-destructive text-white shadow-btn hover:bg-destructive/90 hover:shadow-btn-hover',
        outline: 'border border-border bg-card text-foreground shadow-btn hover:bg-secondary',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
        ghost: 'text-ted-ink-soft hover:bg-secondary',
        link: 'text-ted-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-[18px] text-sm has-[>svg]:px-4',
        xs: "h-7 gap-1 px-3 text-xs has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 px-3.5 text-[12.5px] has-[>svg]:px-3',
        lg: 'h-12 px-6 text-[15px] has-[>svg]:px-5',
        icon: 'size-10',
        'icon-xs': "size-7 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button };
